import type { Checklist } from '@cognite/apm-client';
import dayjs from 'dayjs';

import type { DataGridChecklist } from '../types';

export const checklistToSchedulesDataGridRow = (
  checklist: Checklist
): DataGridChecklist => {
  return {
    ...checklist,
    id: checklist.externalId,
    day: checklist.startTime
      ? dayjs(checklist.startTime).format('dddd - DD MMM YYYY')
      : undefined,
  };
};

/**
 * Utility type to extract keys from T whose values are string or undefined.
 * This ensures that the 'key' parameter passed to the sort functions refers
 * to a property that is suitable for localeCompare.
 *
 * @template T The type of the object.
 */
export type SortableStringKeys<T> = {
  [K in keyof T]: T[K] extends string | undefined ? K : never;
}[keyof T];

/**
 * Sorts an array of DataGridChecklist objects by a specified string property
 * in a locale-sensitive manner. Handles cases where the property might be
 * missing (undefined) on some objects, consistently placing them
 * before valid string values.
 *
 * @param {Array<DataGridChecklist>} data The array of objects to sort.
 * @param {SortableStringKeys<DataGridChecklist>} key The name of the string property to sort by (e.g., 'startTime', 'title').
 * @returns {Array<DataGridChecklist>} A new array with the sorted objects.
 */
export const sortByStringProperty = (
  data: DataGridChecklist[],
  key: SortableStringKeys<DataGridChecklist>
): DataGridChecklist[] => {
  const sortedData: DataGridChecklist[] = [...data];
  sortedData.sort((a: DataGridChecklist, b: DataGridChecklist) => {
    const valueA = a[key as keyof DataGridChecklist];
    const valueB = b[key as keyof DataGridChecklist];

    // Case 1: Both are strings
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return valueA.localeCompare(valueB);
    }
    // Case 2: Ensure undefined values are sorted before strings
    else if (typeof valueA === 'string' && valueB === undefined) {
      return 1;
    } else if (valueA === undefined && typeof valueB === 'string') {
      return -1;
    }
    // Case 3: Both are undefined, maintain relative order
    else {
      return 0;
    }
  });
  return sortedData;
};

/**
 * Sorts an array of DataGridChecklist objects by multiple string properties
 * in a locale-sensitive manner. The sort order is determined by the order of keys provided.
 * Handles cases where properties might be missing (undefined), consistently
 * placing them before valid string values for each comparison level.
 *
 * @param {Array<DataGridChecklist>} data The array of objects to sort.
 * @param {(SortableStringKeys<DataGridChecklist>)[]} keys An array of string property names to sort by, in order of precedence.
 * @returns {Array<DataGridChecklist>} A new array with the sorted objects.
 */
export const sortByMultipleStringProperties = (
  data: DataGridChecklist[],
  keys: SortableStringKeys<DataGridChecklist>[]
): DataGridChecklist[] => {
  const sortedData: DataGridChecklist[] = [...data];
  sortedData.sort((a: DataGridChecklist, b: DataGridChecklist) => {
    for (const key of keys) {
      const valueA = a[key as keyof DataGridChecklist];
      const valueB = b[key as keyof DataGridChecklist];

      // Case 1: Both are strings
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const comparison = valueA.localeCompare(valueB);
        if (comparison !== 0) {
          return comparison;
        }
      }
      // Case 2: Ensure undefined values are sorted before strings
      else if (typeof valueA === 'string' && valueB === undefined) {
        return 1;
      } else if (valueA === undefined && typeof valueB === 'string') {
        return -1;
      }
      // If both are undefined, the loop will simply continue to the next key.
    }
    return 0; // If all keys are equal or missing, elements are considered equal
  });
  return sortedData;
};
